<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import ordersFilterItems from '../ordersFilterItems';

import OrdersHeader from './OrdersHeader.vue';
import OrdersFilter from './OrdersFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import { useRoute, useRouter } from 'vue-router';

defineProps({
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, default: '' },
  hasAppliedFilters: { type: Boolean, default: false },
  activeSort: { type: String, default: 'placed_at' },
  activeOrdering: { type: String, default: '-' },
  showColumnConfig: { type: Boolean, default: false },
});

const emit = defineEmits([
  'search',
  'applyFilter',
  'clearFilters',
  'update:sort',
  'openColumnConfig',
]);

const FILTER_TYPE_ORDER = 3;

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const appliedFilters = useMapGetter('orders/getAppliedOrderFilters');
const getInboxes = useMapGetter('inboxes/getInboxes');
const getOrderCustomViews = useMapGetter('customViews/getOrderCustomViews');

const showFiltersModal = ref(false);
const showSaveFilterModal = ref(false);
const showDeleteModal = ref(false);
const savedFilterQuery = ref({});

const activeSegmentId = computed(() => route.params.segmentId);
const isSegmentRoute = computed(() => route.name === 'orders_segments_index');
const activeSegment = computed(() => {
  const id = Number(activeSegmentId.value);
  const views = getOrderCustomViews.value || [];
  return views.find(v => Number(v.id) === id) || null;
});

const handleNewOrder = () => {
  useAlert(t('ORDERS_LAYOUT.HEADER.ACTIONS.COMING_SOON.ORDERS'), { type: 'info' });
};

const handleImport = () => {
  useAlert(t('ORDERS_LAYOUT.HEADER.ACTIONS.COMING_SOON.IMPORT'), { type: 'info' });
};

const handleExport = () => {
  useAlert(t('ORDERS_LAYOUT.HEADER.ACTIONS.COMING_SOON.EXPORT'), { type: 'info' });
};

const closeAdvanceFiltersModal = () => {
  showFiltersModal.value = false;
};

const onApplyFilter = async payload => {
  const query = filterQueryGenerator(payload);
  savedFilterQuery.value = query;
  emit('applyFilter', query);
  showFiltersModal.value = false;
};

const onClearFilters = () => {
  emit('clearFilters');
  showFiltersModal.value = false;
};

const onToggleFilters = () => {
  showFiltersModal.value = !showFiltersModal.value;
};

const onSaveFilter = () => {
  if (
    !savedFilterQuery.value?.payload?.length &&
    appliedFilters.value?.length
  ) {
    savedFilterQuery.value = filterQueryGenerator(appliedFilters.value);
  }
  showSaveFilterModal.value = true;
};

const onCloseSaveFilter = () => {
  showSaveFilterModal.value = false;
};

const onDeleteSegment = () => {
  if (!activeSegment.value) return;
  showDeleteModal.value = true;
};

const openLastSavedItem = () => {
  // After save, navigate to the newly created segment (last item)
  const views = getOrderCustomViews.value || [];
  if (views.length) {
    const last = views[views.length - 1];
    router.push({
      name: 'orders_segments_index',
      params: { segmentId: last.id },
    });
  }
};

// Build dynamic filter items with inbox options
const ordersFilterItemsWithInboxes = () => {
  const inboxOptions = (getInboxes.value || []).map(inb => ({
    id: inb.id,
    name: inb.name,
  }));
  return ordersFilterItems.map(item => {
    if (item.attributeKey === 'inbox_id') {
      return { ...item, options: inboxOptions };
    }
    return item;
  });
};

defineExpose({
  onToggleFilters,
});
</script>

<template>
  <div>
    <OrdersHeader
      :header-title="headerTitle"
      :search-value="searchValue"
      :has-applied-filters="hasAppliedFilters"
      :active-sort="activeSort"
      :active-ordering="activeOrdering"
      @newOrder="handleNewOrder"
      @import="handleImport"
      @export="handleExport"
      @toggleAdvanceFilters="onToggleFilters"
      @search="emit('search', $event)"
      @update:sort="emit('update:sort', $event)"
      @saveFilter="onSaveFilter"
      @openColumnConfig="emit('openColumnConfig')"
    >
      <template #filter>
        <div
          v-if="showFiltersModal"
          class="fixed left-1/2 -translate-x-1/2 top-16 w-[min(92vw,48rem)] z-50 md:fixed md:left-1/2 md:-translate-x-1/2 md:w-[calc(100vw-16px)] md:top-16 md:mt-1 lg:absolute lg:top-full lg:left-auto lg:-translate-x-0 lg:ltr:right-0 lg:rtl:left-0 lg:w-auto"
        >
          <OrdersFilter
            :filter-types="ordersFilterItemsWithInboxes()"
            :applied-filters="appliedFilters"
            class="w-full"
            @applyFilter="onApplyFilter"
            @clearFilters="onClearFilters"
            @close="closeAdvanceFiltersModal"
          />
        </div>
      </template>
    </OrdersHeader>

    <div
      v-if="isSegmentRoute && activeSegment"
      class="flex items-center gap-2 px-6 py-2"
    >
      <woot-button
        size="small"
        icon="edit"
        variant="outline"
        @click="onSaveFilter"
      >
        {{ $t('FILTER.CUSTOM_VIEWS.EDIT') }}
      </woot-button>
      <woot-button
        size="small"
        icon="delete"
        variant="outline"
        @click="onDeleteSegment"
      >
        {{ $t('FILTER.CUSTOM_VIEWS.DELETE.BUTTON') }}
      </woot-button>
    </div>

    <Teleport v-if="showSaveFilterModal" to="#saveFilterTeleportTarget">
      <SaveCustomView
        :filter-type="FILTER_TYPE_ORDER"
        :custom-views-query="savedFilterQuery"
        :open-last-saved-item="openLastSavedItem"
        @close="onCloseSaveFilter"
      />
    </Teleport>

    <DeleteCustomViews
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :active-custom-view="activeSegment"
      :custom-views-id="activeSegment?.id"
      :active-filter-type="FILTER_TYPE_ORDER"
      :open-last-item-after-delete="() => router.push({ name: 'orders_list' })"
      @close="() => (showDeleteModal.value = false)"
    />
  </div>
</template>
